import { memo } from "react";
import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { OPS } from "@/constants/testIds";
import { fmt, fmtCost, fmtMoney, fmtQuantity, isCounted, sanitizeQuantity } from "./scUtils";

const S = OPS.sc.sheet;

// ------------------------------------------------------------------ line model
// A line on screen is an ENTRY of input strings: `{q}` for a single quantity,
// `{p, l}` (whole packs + loose count units) for an item with a pack helper.
// "0" IS a count; only "" is "not counted" (never `v && v > 0`).

/** The item is counted in packs + loose units. */
export const isPackItem = (item) => !!item?.pack_unit && Number(item?.pack_size) > 0;

/** A (restored) entry has the shape this item takes today. A draft saved before
 *  finance added or removed the pack helper must never be sent: `{q}` read as
 *  packs + loose would be blank and CLEAR the stored line. */
export function entryFits(item, entry) {
    if (!entry || typeof entry !== "object") return false;
    const str = (v) => typeof v === "string";
    return isPackItem(item) ? str(entry.p) && str(entry.l) : str(entry.q);
}

/** "5." -> "5", "." -> "", ".5" -> "0.5": what the server's quantity rule accepts. */
export function cleanQty(s) {
    let v = String(s ?? "");
    if (v === ".") return "";
    if (v.endsWith(".")) v = v.slice(0, -1);
    if (v.startsWith(".")) v = `0${v}`;
    return v;
}

const num = (s) => {
    const v = cleanQty(s);
    if (!isCounted(v)) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
};

const round3 = (n) => Math.round(n * 1000) / 1000;

/** The entry the SERVER's line implies (what the inputs show with no edit). */
export function serverEntry(item, line) {
    if (!isPackItem(item)) return { q: line ? String(line.quantity ?? "") : "" };
    if (!line) return { p: "", l: "" };
    if (line.packs !== null && line.packs !== undefined) {
        return { p: String(line.packs), l: line.loose !== null && line.loose !== undefined ? String(line.loose) : "" };
    }
    // a line saved as a plain quantity (e.g. «اعتبرها صفراً»): all of it is loose
    return { p: "", l: String(line.quantity ?? "") };
}

/** Total in count units, or null when the entry is blank (= not counted / clear). */
export function entryTotal(item, entry) {
    if (!entry) return null;
    if (!isPackItem(item)) return num(entry.q);
    const p = num(entry.p);
    const l = num(entry.l);
    if (p === null && l === null) return null;
    return round3((p || 0) * Number(item.pack_size) + (l || 0));
}

/** Two entries mean the same stored line. */
export function sameEntry(item, a, b) {
    if (!isPackItem(item)) return num(a?.q) === num(b?.q);
    const blankA = num(a?.p) === null && num(a?.l) === null;
    const blankB = num(b?.p) === null && num(b?.l) === null;
    if (blankA || blankB) return blankA === blankB;
    return (num(a.p) || 0) === (num(b.p) || 0) && (num(a.l) || 0) === (num(b.l) || 0);
}

/** The same input TEXT (not just the same quantity): "5." typed over a stored 5
 *  means the same line but is still being typed, so it must stay on screen. */
export function sameText(item, a, b) {
    if (!a || !b) return a === b;
    return isPackItem(item) ? a.p === b.p && a.l === b.l : a.q === b.q;
}

/** One `PUT /sc/sheet/lines` line; blank strings clear the stored line. */
export function toPayload(item, entry) {
    if (!isPackItem(item)) return { item_id: item.id, quantity: cleanQty(entry.q) };
    return { item_id: item.id, packs: cleanQty(entry.p), loose: cleanQty(entry.l) };
}

/** The sheet line the server stores for a saved entry (same rule as
 *  sc_core.line_from_input), or null when the entry clears the line. Lets the
 *  screen show a save at once while the sheet reloads for values. */
export function savedLine(item, entry) {
    const total = entryTotal(item, entry);
    if (total === null) return null;
    const base = { quantity: total, unit: item.count_unit, at: null, by: null };
    if (!isPackItem(item)) return { ...base, packs: null, loose: null };
    return { ...base, packs: Math.floor(num(entry.p) || 0), loose: num(entry.l) || 0 };
}

// ------------------------------------------------------------------- widgets
/**
 * Quantity input with the unit as a suffix. RTL-safe: the WRAPPER is
 * `dir="ltr"`, so `end-3` and the input's `pe-*` are the same physical side and
 * the Arabic unit label never sits over the digits.
 */
function QtyField({ value, onChange, unit, allowsDecimal, testId, invalid, onEnter, label }) {
    const { t } = useI18n();
    return (
        <div className="relative min-w-0" dir="ltr">
            <Input
                data-testid={testId}
                data-sc-qty=""
                value={value ?? ""}
                onChange={(e) => onChange(sanitizeQuantity(e.target.value, allowsDecimal))}
                onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); onEnter && onEnter(e.currentTarget); }
                }}
                inputMode={allowsDecimal ? "decimal" : "numeric"}
                enterKeyHint="next"
                autoComplete="off"
                aria-label={label}
                aria-invalid={invalid ? "true" : undefined}
                placeholder={t("stk_qty_placeholder")}
                dir="ltr"
                className={`h-11 pe-16 text-base font-semibold tabular-nums ${
                    invalid ? "border-[color:var(--danger)]" : ""
                }`}
            />
            {unit ? (
                <span className="pointer-events-none absolute inset-y-0 end-3 flex max-w-[3.5rem] items-center">
                    <span className="truncate text-xs font-medium text-[color:var(--ink-muted)]">{unit}</span>
                </span>
            ) : null}
        </div>
    );
}

/** Read-only quantity: «٢ كرتون + ٣ حبة» or «١٢٫٥ كيلو», «—» when not counted. */
function QtyText({ item, line, testId }) {
    const { t, lang } = useI18n();
    if (!line) {
        return (
            <span data-testid={testId} data-counted="false"
                  className="text-sm font-semibold text-[color:var(--ink-faint)]">
                {t("stk_not_counted_short")}
            </span>
        );
    }
    const packs = isPackItem(item) && line.packs !== null && line.packs !== undefined && line.packs > 0;
    return (
        <span data-testid={testId} data-counted="true" className="text-sm font-bold text-[color:var(--ink)]">
            {packs ? (
                <span className="me-1 text-xs font-medium text-[color:var(--ink-muted)]">
                    <bdi dir="ltr">{fmtQuantity(line.packs, lang)}</bdi> {item.pack_unit}
                    {line.loose ? <> + <bdi dir="ltr">{fmtQuantity(line.loose, lang)}</bdi> {item.count_unit}</> : null}
                    {" = "}
                </span>
            ) : null}
            <bdi dir="ltr">{fmtQuantity(line.quantity, lang)}</bdi> {line.unit || item.count_unit}
        </span>
    );
}

/** The value under a counted line (only when the sheet carries values). The
 *  server prices a saved line per the unit it was SAVED in (`line.unit`), which
 *  differs from `item.count_unit` once finance has changed the count unit. */
function LineValue({ item, line, value }) {
    const { t, lang } = useI18n();
    if (!value) return null;
    if (value.value === null || value.value === undefined) {
        return (
            <p data-testid={S.lineValue(item.id)} data-value=""
               className="mt-1 text-[11px] text-[color:var(--ink-faint)]">
                {t("stk_no_cost")}
            </p>
        );
    }
    return (
        <p data-testid={S.lineValue(item.id)} data-value={value.value}
           className="mt-1 text-[11px] font-semibold text-[color:var(--brand-brown)]">
            {/* money carries its currency: an auto-direction isolate keeps «١٢٫٥٠ ر.س» in Arabic order */}
            <bdi>{fmtMoney(value.value, lang)}</bdi>
            {value.unitCost !== null && value.unitCost !== undefined ? (
                <span className="ms-1.5 font-normal text-[color:var(--ink-faint)]">
                    <bdi>{fmt(t("stk_unit_cost"), { cost: fmtCost(value.unitCost, lang), unit: line?.unit || item.count_unit })}</bdi>
                </span>
            ) : null}
        </p>
    );
}

/**
 * One count line. Editable: one quantity input with the count unit, or the
 * packs + loose pair with the computed total. Read-only: the stored quantity
 * as text. `value` is `{value, unitCost}` (SAR) or null when values are hidden.
 */
export const CountLine = memo(function CountLine({
    item, entry, line, dirty, error, value, editable, onChange, onEnter,
}) {
    const { t, tn, lang } = useI18n();
    const pack = isPackItem(item);
    const total = entryTotal(item, entry);
    const counted = editable ? total !== null : !!line;
    const errField = error?.field;
    const errText = error ? (lang === "ar" ? error.message_ar || error.message_en : error.message_en || error.message_ar) : null;

    const head = (
        <div className="min-w-0">
            <p className="flex items-start gap-1.5 text-sm font-semibold leading-snug text-[color:var(--ink)]">
                {dirty ? (
                    <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[color:var(--brand-orange)]" />
                ) : null}
                <span className="min-w-0 break-words">{tn(item)}</span>
                {item.is_active === false ? (
                    <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-[10px]">{t("stk_inactive")}</Badge>
                ) : null}
            </p>
            <p className="mt-0.5 text-[11px] text-[color:var(--ink-faint)]">
                <bdi dir="ltr">{item.code}</bdi>
                {pack ? (
                    <span className="ms-1.5">
                        · <bdi>{fmt(t("stk_pack_hint"), {
                            pack: item.pack_unit, n: fmtQuantity(item.pack_size, lang), unit: item.count_unit,
                        })}</bdi>
                    </span>
                ) : null}
            </p>
        </div>
    );

    const foot = (
        <>
            {counted ? <LineValue item={item} line={line} value={value} /> : null}
            {errText ? (
                <p data-testid={S.lineError(item.id)} className="mt-1 text-[11px] font-semibold text-[color:var(--danger)]">
                    {errText}
                </p>
            ) : null}
        </>
    );

    const rowClass = `border-b border-[color:var(--separator)] px-1 py-2.5 ${
        error ? "bg-[color:rgba(220,38,38,0.05)]" : ""
    }`;
    const common = {
        "data-testid": S.line(item.id),
        "data-counted": counted ? "true" : "false",
        "data-dirty": dirty ? "true" : "false",
    };

    if (!editable) {
        return (
            <div {...common} className={`grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2 ${rowClass}`}>
                <div className="min-w-0">{head}{foot}</div>
                <div className="pt-0.5 text-end"><QtyText item={item} line={line} testId={S.qty(item.id)} /></div>
            </div>
        );
    }

    if (!pack) {
        return (
            <div {...common} className={`grid grid-cols-[minmax(0,1fr)_9.5rem] items-start gap-2 ${rowClass}`}>
                <div className="min-w-0">{head}{foot}</div>
                <QtyField testId={S.qty(item.id)} value={entry?.q} unit={item.count_unit}
                          allowsDecimal={!!item.allows_decimal} invalid={!!error} label={tn(item)}
                          onChange={(v) => onChange(item, { q: v })} onEnter={onEnter} />
            </div>
        );
    }

    return (
        <div {...common} className={rowClass}>
            <div className="flex items-start justify-between gap-2">
                {head}
                <span data-testid={S.qty(item.id)} data-counted={counted ? "true" : "false"}
                      className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${
                          counted
                              ? "bg-[color:var(--brand-orange-soft)] text-[color:var(--brand-orange-soft-fg)]"
                              : "bg-[color:var(--bg-surface-2)] text-[color:var(--ink-faint)]"
                      }`}>
                    {counted ? <>= <bdi dir="ltr">{fmtQuantity(total, lang)}</bdi> {item.count_unit}</> : t("stk_not_counted_short")}
                </span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
                <label className="min-w-0 space-y-1">
                    <span className="block text-[11px] font-semibold text-[color:var(--ink-muted)]">{t("stk_packs")}</span>
                    <QtyField testId={S.packs(item.id)} value={entry?.p} unit={item.pack_unit} allowsDecimal={false}
                              invalid={!!error && errField !== "loose"} label={`${tn(item)} — ${t("stk_packs")}`}
                              onChange={(v) => onChange(item, { p: v, l: entry?.l ?? "" })} onEnter={onEnter} />
                </label>
                <label className="min-w-0 space-y-1">
                    <span className="block text-[11px] font-semibold text-[color:var(--ink-muted)]">{t("stk_loose")}</span>
                    <QtyField testId={S.loose(item.id)} value={entry?.l} unit={item.count_unit}
                              allowsDecimal={!!item.allows_decimal}
                              invalid={!!error && errField !== "packs"} label={`${tn(item)} — ${t("stk_loose")}`}
                              onChange={(v) => onChange(item, { p: entry?.p ?? "", l: v })} onEnter={onEnter} />
                </label>
            </div>
            {foot}
        </div>
    );
});
